(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * IV Store — Brinde automático por valor de carrinho
   * VERSÃO FINAL (set/2026) — base: iv-store-brindes.FIXED.js
   *
   * Correções desta versão em relação à FIXED:
   *  1. FONTE ÚNICA: consome window.IVBrindes.config (só brindes ATIVOS
   *     e COM ESTOQUE) e window.IVBrindes.allVariantIds (todos, inclusive
   *     esgotados) para detectar/remover órfãos.
   *  2. ESTOQUE: nunca tenta adicionar um brinde fora de `config`
   *     (portanto nunca adiciona esgotado). Se um brinde esgotou e ficou
   *     órfão no carrinho, ele é removido.
   *  3. ACÚMULO: escolhe UM brinde por vez (o de maior prioridade / maior
   *     valor cruzado). O frete grátis NÃO é produto-brinde, então o Ecobag
   *     (brinde de maior prioridade) permanece ao cruzar a faixa do frete.
   *
   * Preservado da FIXED: sem sequestro de window.fetch; sync acionado só
   * por eventos do tema (cart:updated / cart:item-added / CartDrawer:open)
   * com trava de reentrância (_processing) + cooldown + debounce; trava
   * dos botões do item-brinde + selo "Brinde Exclusivo".
   * ------------------------------------------------------------------ */

  function getConfig() {
    // brindes elegíveis (ativos + com estoque)
    return (window.IVBrindes && window.IVBrindes.config) || [];
  }

  function getAllGiftIds() {
    // TODOS os variantIds de brinde (inclusive esgotados/inativos) para
    // detecção e remoção. Cai para os ids do config se allVariantIds faltar.
    if (window.IVBrindes && Array.isArray(window.IVBrindes.allVariantIds) && window.IVBrindes.allVariantIds.length) {
      return window.IVBrindes.allVariantIds.slice();
    }
    return getConfig().map(function (b) { return b.variantId; });
  }

  function getCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function cartChange(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  function cartAdd(variantId) {
    // Segurança extra: só adiciona variante presente no config (ativa+estoque).
    var ok = getConfig().some(function (b) { return b.variantId === variantId; });
    if (!ok) { return Promise.resolve(null); }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _brinde: 'sim' }
      })
    }).then(function (r) { return r.json(); });
  }

  function getPaidSubtotal(cart) {
    var giftIds = getAllGiftIds();
    var total = 0;
    cart.items.forEach(function (item) {
      if (giftIds.indexOf(item.variant_id) === -1) {
        total += item.final_line_price;
      }
    });
    return total;
  }

  function getCorrectGift(subtotalCents) {
    var config = getConfig();
    if (!config.length) return null;
    var elegiveis = config.filter(function (b) {
      return b.available !== false && subtotalCents >= b.valorMinimo;
    });
    if (!elegiveis.length) return null;
    // maior prioridade vence; empate -> maior valorMinimo (faixa mais alta cruzada)
    elegiveis.sort(function (a, b) {
      var d = (b.prioridade || 0) - (a.prioridade || 0);
      if (d !== 0) return d;
      return (b.valorMinimo || 0) - (a.valorMinimo || 0);
    });
    return elegiveis[0].variantId;
  }

  function getGiftsInCart(cart) {
    var giftIds = getAllGiftIds();
    return cart.items
      .filter(function (item) { return giftIds.indexOf(item.variant_id) !== -1; })
      .map(function (item) { return { key: item.key, variantId: item.variant_id }; });
  }

  /* -------- trava de reentrância + debounce (mata o loop) -------- */
  var _processing = false;      // true durante todo o ciclo de sync + cooldown
  var _debounceTimer = null;
  var COOLDOWN_MS = 400;        // ignora ecos gerados pelo próprio refresh
  var DEBOUNCE_MS = 300;        // agrupa cliques rápidos num único sync

  function scheduleSyncGifts() {
    if (_debounceTimer) clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(runSyncGifts, DEBOUNCE_MS);
  }

  function runSyncGifts() {
    if (!getConfig().length && !getAllGiftIds().length) return;
    // Se um sync está em andamento, NÃO dispara outro: re-agenda uma vez
    // para rodar assim que o atual terminar. É isto que quebra o loop.
    if (_processing) { scheduleSyncGifts(); return; }
    _processing = true;

    getCart().then(function (cart) {
      var subtotal      = getPaidSubtotal(cart);
      var correctGiftId = getCorrectGift(subtotal);
      var giftsInCart   = getGiftsInCart(cart);

      // Remove todo brinde que não seja o correto — inclusive um brinde
      // que esgotou (saiu do config) e ficou órfão no carrinho.
      var toRemove = giftsInCart.filter(function (g) {
        return g.variantId !== correctGiftId;
      });

      var alreadyCorrect = giftsInCart.some(function (g) {
        return g.variantId === correctGiftId;
      });

      // Nada mudou em termos de brinde? Encerra sem tocar no carrinho.
      if (!toRemove.length && (alreadyCorrect || !correctGiftId)) {
        return false; // nada a fazer
      }

      var chain = Promise.resolve();
      toRemove.forEach(function (g) {
        chain = chain.then(function () { return cartChange(g.key, 0); });
      });

      return chain.then(function () {
        if (correctGiftId && !alreadyCorrect) {
          return cartAdd(correctGiftId);
        }
      }).then(function () { return true; }); // mexeu no carrinho
    }).then(function (mudou) {
      if (mudou) return refreshCartUI();
      applyGiftStyles();
    }).then(function () {
      // cooldown: só libera depois de um respiro, ignorando os eventos
      // que o próprio refresh/re-render possa ter gerado.
      setTimeout(function () { _processing = false; }, COOLDOWN_MS);
    }).catch(function (err) {
      console.error('[IV Brindes]', err);
      setTimeout(function () { _processing = false; }, COOLDOWN_MS);
    });
  }

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      document.querySelectorAll('[data-cart-count], .m-badge__count, .m-header__cart-count')
        .forEach(function (el) { el.textContent = cart.item_count; });
      applyGiftStyles();
    });
  }

  function applyGiftStyles() {
    var giftIds = getAllGiftIds();
    document.querySelectorAll('[data-cart-item]').forEach(function (el) {
      var input = el.querySelector('input[data-quantity-variant-id]');
      if (!input) return;
      var variantId = parseInt(input.getAttribute('data-quantity-variant-id'), 10);
      if (giftIds.indexOf(variantId) === -1) return;

      el.querySelectorAll('.m-quantity__button').forEach(function (btn) {
        btn.disabled = true;
        btn.style.cssText += ';opacity:.3;cursor:not-allowed;pointer-events:none';
      });
      input.disabled = true;
      input.readOnly = true;

      el.querySelectorAll('m-cart-remove-button, [data-role="remove-item"]')
        .forEach(function (btn) { btn.style.display = 'none'; });

      var info = el.querySelector('.m-cart-item__info');
      if (info && !info.querySelector('.iv-brinde-badge')) {
        var badge = document.createElement('span');
        badge.className = 'iv-brinde-badge';
        badge.textContent = 'Brinde Exclusivo';
        badge.style.cssText = 'display:inline-block;background:#eef3ec;color:#3f5140;font-size:11px;font-weight:700;padding:2px 10px;border-radius:20px;margin-top:4px;letter-spacing:.3px;border:1px solid #cfe0cb';
        info.appendChild(badge);
      }
    });
  }

  function observeCartDrawer() {
    var observer = new MutationObserver(function () {
      setTimeout(applyGiftStyles, 120);
    });
    function attach() {
      var drawer = document.querySelector(
        '.m-cart-drawer, [data-cart-drawer], #CartDrawer, .cart-drawer'
      );
      if (drawer) observer.observe(drawer, { childList: true, subtree: true });
    }
    attach();
    window.addEventListener('load', attach);
  }

  function bindEvents() {
    // Aciona o sync APENAS por eventos reais do tema (mudança do usuário).
    // As mutações do próprio brinde usam fetch cru e NÃO disparam estes
    // eventos — por isso não há mais loop.
    document.addEventListener('cart:updated', scheduleSyncGifts);
    document.addEventListener('cart:item-added', scheduleSyncGifts);
    // Ao abrir a sacola, garante brinde correto antes do checkout.
    document.addEventListener('CartDrawer:open', function () {
      applyGiftStyles();
      scheduleSyncGifts();
    });
  }

  function init() {
    bindEvents();
    observeCartDrawer();
    runSyncGifts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
